<script setup lang="ts">
import { watch, nextTick, ref, onUnmounted } from 'vue';
import { confirmState, settleConfirm } from '../game/confirm';

/** One dialog for the whole game — see confirm.ts. Mounted once in HeroScreen. */
const confirmBtn = ref<HTMLButtonElement | null>(null);

function onKey(e: KeyboardEvent) {
  if (!confirmState.open) return;
  if (e.key === 'Escape') { e.preventDefault(); settleConfirm(false); }
}
window.addEventListener('keydown', onKey);
onUnmounted(() => window.removeEventListener('keydown', onKey));

watch(() => confirmState.open, async (open) => {
  if (open) { await nextTick(); confirmBtn.value?.focus(); }
});
</script>

<template>
  <Transition name="cfh">
    <div
      v-if="confirmState.open && confirmState.opts"
      class="fixed inset-0 z-[400] grid place-items-center p-4"
      style="background: rgba(6, 4, 12, 0.72); backdrop-filter: blur(3px)"
      @click.self="settleConfirm(false)"
    >
      <div class="cfh-card corner-frame relative" :class="{ 'cfh-danger': confirmState.opts.danger }">
        <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
        <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>

        <div class="font-label uppercase tracking-[0.3em] text-[9px] text-text-dim">
          {{ confirmState.opts.kicker ?? 'The house asks' }}
        </div>

        <img v-if="confirmState.opts.icon" :src="confirmState.opts.icon" alt="" class="cfh-art" />

        <h2 class="font-heading text-xl mt-1" :class="confirmState.opts.danger ? 'text-error' : 'text-gold'">
          {{ confirmState.opts.title }}
        </h2>

        <div v-if="confirmState.opts.rows?.length" class="cfh-rows">
          <div v-for="(r, i) in confirmState.opts.rows" :key="i" class="cfh-row">
            <span class="cfh-row-label">{{ r.label }}</span>
            <span
              class="cfh-row-value"
              :class="{
                'text-gold': r.tone === 'gold' || !r.tone,
                'text-gold-bright': r.tone === 'bright',
                'text-error': r.tone === 'error',
                'text-success': r.tone === 'success',
                'text-text-dim': r.tone === 'dim',
              }"
            >
              <img v-if="r.icon" :src="r.icon" alt="" />
              {{ r.value }}
            </span>
          </div>
        </div>

        <p v-if="confirmState.opts.note" class="font-body italic text-xs text-text-dim mt-2">
          {{ confirmState.opts.note }}
        </p>

        <div class="flex items-center justify-center gap-2 mt-4">
          <button class="btn-deco btn-deco--ghost" @click="settleConfirm(false)">
            {{ confirmState.opts.cancelLabel ?? 'Walk away' }}
          </button>
          <button
            ref="confirmBtn"
            class="btn-deco"
            :class="confirmState.opts.danger ? 'btn-deco--blood' : 'btn-deco--gold'"
            @click="settleConfirm(true)"
          >
            {{ confirmState.opts.confirmLabel ?? 'Confirm' }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.cfh-card {
  width: min(380px, 92vw);
  padding: 22px 22px 18px;
  text-align: center;
  border: 1px solid color-mix(in srgb, var(--color-gold, #c9a84c) 45%, transparent);
  border-radius: 12px;
  background:
    radial-gradient(120% 90% at 50% 0%, rgba(200, 162, 76, 0.1), transparent 55%),
    var(--color-surface, #14121a);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.7);
  animation: cfh-rise 220ms ease both;
}
.cfh-danger {
  border-color: color-mix(in srgb, var(--color-error, #b3413e) 60%, transparent);
  background:
    radial-gradient(120% 90% at 50% 0%, rgba(179, 65, 62, 0.1), transparent 55%),
    var(--color-surface, #14121a);
}
@keyframes cfh-rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }

.cfh-art {
  width: 64px; height: 64px;
  margin: 10px auto 0;
  object-fit: contain;
  filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.6));
}

.cfh-rows {
  margin-top: 12px;
  border-top: 1px solid color-mix(in srgb, var(--color-border) 70%, transparent);
}
.cfh-row {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  padding: 7px 2px;
  border-bottom: 1px solid color-mix(in srgb, var(--color-border) 55%, transparent);
}
.cfh-row-label {
  font-family: var(--font-label, sans-serif);
  font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase;
  color: var(--color-text-dim);
}
.cfh-row-value {
  display: inline-flex; align-items: center; gap: 6px;
  font-family: var(--font-heading, serif);
  font-size: 15px; font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.cfh-row-value img { width: 16px; height: 16px; object-fit: contain; }

.cfh-enter-active, .cfh-leave-active { transition: opacity 160ms ease; }
.cfh-enter-from, .cfh-leave-to { opacity: 0; }
@media (prefers-reduced-motion: reduce) {
  .cfh-card { animation: none; }
  .cfh-enter-active, .cfh-leave-active { transition: none; }
}
</style>
